import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Fetch_product_catalogue } from "../../hooks/APIHooks";
import LogoLoader from "../../components/LogoLoader/LogoLoader";
import { IoSearchOutline } from "react-icons/io5";

import lazy_loaderwebp from '../../assets/lazy_loaderwebp.webp'

const Store = () => {
  const [catalogueData, setCatalogueData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [imageLoaded, setImageLoaded] = useState({});
  const [imageErrors, setImageErrors] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(Fetch_product_catalogue);
        if (!response.ok) {
          throw new Error("Failed to fetch data");
        }
        const data = await response.json();

        if (data.success) {
          setCatalogueData(data.data);
          setLoading(false);
        } else {
          throw new Error(data.error || "An error occurred");
        }
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getImageUrl = (imagePath) => {
    if (!imagePath) {
      return '';
    }
    
    if (imagePath.startsWith('http')) return imagePath;
    
    const cleanPath = imagePath.replace(/^(\.?\/?)?/, '');
    
    // Handle different environments
    let baseUrl;
    const { hostname, port, protocol } = window.location;
    
    if (hostname === 'localhost' && port === '3000') {
      // React dev server on port 3000, APIs on port 80
      baseUrl = `${protocol}//${hostname}`;
    } else if (hostname === 'localhost' && !port) {
      // React build served from same domain as APIs
      baseUrl = window.location.origin;
    } else {
      // Production environment
      baseUrl = window.location.origin;
    }
    
    return `${baseUrl}/apis/${cleanPath}`;
  };

  const handleImageLoad = (id) => {
    setImageLoaded((prev) => ({ ...prev, [id]: true }));
    setImageErrors((prev) => ({ ...prev, [id]: false }));
  };

  const handleImageError = (id) => {
    setImageErrors((prev) => ({ ...prev, [id]: true }));
    setImageLoaded((prev) => ({ ...prev, [id]: true }));
  };

  const getFallbackContent = (item) => {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-200 rounded-lg">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-600 mb-2">
            {item.catalogue_name ? item.catalogue_name.charAt(0).toUpperCase() : '?'}
          </div>
          <div className="text-xs text-gray-500 px-2">
            {item.catalogue_name || 'No Name'}
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="w-full overflow-hidden">
      {loading ? (
        <div className="flex justify-center items-center h-[100vh]">
          <LogoLoader />
        </div>
      ) : error ? (
        <div className="text-center text-red-500 py-8">{error}</div>
      ) : (
        <div className="grid grid-cols-3 lg:grid-cols-5 gap-4 p-4">
          {catalogueData.map((item) => (
            <Link
              key={item.catalogue_id}
              to={`/Store/${encodeURIComponent(item.catalogue_name)}/${item.catalogue_id}`}
              className="relative rounded-lg shadow-md h-[23vh] sm:h-[50vh] block overflow-hidden group hover:shadow-lg transition-shadow duration-300"
            >
              {/* Lazy Loader */}
              <img
                src={lazy_loaderwebp}
                alt="Loading..."
                className="w-full h-full object-cover absolute top-0 left-0 transition-opacity duration-500"
                style={{ opacity: imageLoaded[item.catalogue_id] ? 0 : 1 }}
              />
              
              {/* Main Image or Fallback */}
              {item.catalogue_image && !imageErrors[item.catalogue_id] ? (
                <img
                  src={getImageUrl(item.catalogue_image)}
                  alt={item.catalogue_name || "Unknown Catalogue"}
                  loading="lazy"
                  onLoad={() => handleImageLoad(item.catalogue_id)}
                  onError={() => handleImageError(item.catalogue_id)}
                  className="w-full h-full object-cover transition-opacity duration-500 group-hover:scale-105 transition-transform duration-300"
                  style={{ opacity: imageLoaded[item.catalogue_id] ? 1 : 0 }}
                />
              ) : (
                getFallbackContent(item)
              )}
              
              {/* Overlay with catalogue name */}
              <div className="absolute bottom-0 w-full text-center bg-gradient-to-t from-black/70 to-transparent p-3">
                <h2 className="text-[14px] font-bold text-white truncate">
                  {item.catalogue_name || "Unknown Catalogue"}
                </h2>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
};

export default Store;